import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Grid,
  InputAdornment,
  Link,
  OutlinedInput,
  Typography
} from '@mui/material'
import { type ChangeEvent, useEffect, useState } from 'react'

import {
  AlertDialog,
  MButton,
  Pagination,
  ProgressLoaders,
  VisuallyHiddenInput
} from 'components'
import { ADMIN_CONTACT_EMAIL } from 'config'
import { color } from 'config/color'
import { isEmpty, slice } from 'lodash'
import { useSnackbar } from 'notistack'
import { isAuthorized, useProvideAuth } from 'utils'
import { type AssetType } from 'utils/hooks/types'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import {
  AssetListCard,
  EditAssetCarousel,
  UploadAssetCard
} from './components'

export const Assets: React.FC = () => {
  const isMobile = useMobileBreakpoints()
  const { listAssets, deleteAssets, uploadAssets, handleLogOut } = useProvideAuth()
  const [assetsData, setAssetsData] = useState<AssetType[]>([])
  const [allAssets, setAllAssets] = useState<AssetType[]>([])
  const [selectedAssets, setSelectedAssets] = useState<AssetType[]>([])
  const [assets, setAssets] = useState<AssetType[]>([])
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [pageCount, setPageCount] = useState(0)
  const { enqueueSnackbar } = useSnackbar()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [dialogData, setDialogData] = useState<any>({
    type: '',
    isOpen: false,
    title: '',
    message: ''
  })

  const [confirmDialog, setConfirmDialog] = useState<any>({
    type: 'warning',
    isOpen: false,
    title: 'Deleting Assets',
    message:
      'You’re about to remove an asset or several assets. Please be aware that this action can’t be undone.'
  })

  const [editCarouselData, setEditCarouselData] = useState<any>({
    isOpen: false,
    failedFiles: [],
    fileData: {}
  })

  const handlePagination = (_e: any, value: any): void => {
    setPage(value)
  }

  const handleSearch = (e: any): void => {
    setSearch(e.target.value)
  }

  const handleCloseCarousel = async (): Promise<void> => {
    setEditCarouselData({ ...editCarouselData, isOpen: false })
    await handleAssets()
  }

  const handleUpload = async (
    event: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    setIsLoading(true)
    const files = event.target.files
    if (files != null) {
      try {
        const formData = new FormData()
        Array.from(files).forEach((file) => {
          formData.append('assets', file)
        })
        const response = await uploadAssets(formData)

        const failedFiles = response.result.filter(
          (item: any) =>
            item.status === 'FAILED' && item.error === 'Duplicate Asset Name'
        )

        if (isEmpty(failedFiles)) {
          setDialogData({
            type: 'success',
            isOpen: true,
            title: 'Assets have been uploaded',
            message:
              'You have successfully uploaded an asset and now its ready to be use in your campaigns.'
          })
        } else {
          setEditCarouselData({
            isOpen: true,
            failedFiles,
            fileData: files
          })
        }
      } catch (error: any) {
        if (!isAuthorized(error)) await handleLogOut()
        setDialogData({
          type: 'error',
          isOpen: true,
          title: 'An error occured',
          message: `We are really sorry but there was a problem and we couldn’t save the asset. Please try again, if the error persist, <a href="mailto:${ADMIN_CONTACT_EMAIL}" >Contact Us</a>. `
        })
      }
    }
    setIsLoading(false)
  }

  const handleMultipleDelete = async (
    showConfirmDialog: boolean
  ): Promise<void> => {
    if (showConfirmDialog) {
      setConfirmDialog({
        ...confirmDialog,
        isOpen: true
      })
      return
    }

    setIsLoading(true)
    try {
      for (const asset of selectedAssets) {
        await deleteAssets(asset.key)
      }
      setDialogData({
        type: 'success',
        isOpen: true,
        title: 'Assets deleted',
        message:
          'You have successfully deleted one or several assets. You can add more assets under Assets'
      })
    } catch (error: any) {
      if (!isAuthorized(error)) await handleLogOut()
      setDialogData({
        type: 'error',
        isOpen: true,
        title: 'An error occured',
        message: `We are really sorry but there was a problem and we couldn’t delete the asset(s). Please try again, if the error persist, <a href="mailto:${ADMIN_CONTACT_EMAIL}" >Contact Us</a>. `
      })
    }

    setIsLoading(false)
  }

  const handleAssets = async (): Promise<void> => {
    try {
      setIsLoading(true)
      const response = await listAssets()
      setAssetsData(response)
      setIsLoading(false)
    } catch (error: any) {
      if (!isAuthorized(error)) await handleLogOut()
      const errMessage = `Error: ${error}`
      enqueueSnackbar(errMessage, { variant: 'error', autoHideDuration: 3000 })
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void handleAssets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setSelectedAssets([])
    setAllAssets(assetsData)
  }, [assetsData])

  useEffect(() => {
    if (isEmpty(search)) {
      setAllAssets(assetsData)
    } else {
      const filteredAssets = assets.filter((item) =>
        item.key.toLowerCase().includes(search.toLowerCase())
      )
      setAllAssets(filteredAssets)
      setPageCount(Math.ceil(filteredAssets.length / 9))
      setPage(1)
    }
  }, [search])

  useEffect(() => {
    setAssets(slice(allAssets, (page - 1) * 9, page * 10 - page))
  }, [allAssets, page])

  useEffect(() => {
    setPageCount(Math.ceil(allAssets.length / 9))
  }, [allAssets, search])

  return (
    <Box mt={5} bgcolor={color.white}>
      <Box
        display="flex"
        p={3}
        gap={1}
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        borderBottom={`solid 1px ${color.secondary.dark[200]}`}
      >
        <Typography color={color.grey[900]} fontWeight={400} fontSize={24}>
          Assets List
        </Typography>

        <MButton
          variant="contained"
          component="label"
          type="button"
          rounded="true"
          sx={{ fontSize: 16 }}
          texttransform="none"
          startIcon={<CloudUploadIcon />}
        >
          <Typography color={color.white} fontWeight={300} fontSize={16}>
            Upload Asset
          </Typography>

          <VisuallyHiddenInput
            type="file"
            accept="image/*"
            onChange={handleUpload}
            multiple
          />
        </MButton>
      </Box>

      <Box display="flex" flexDirection="column" p={3} mb={3}>
        {(assets.length > 0 || !isEmpty(search)) &&
          selectedAssets.length === 0 && (
            <Box display="flex" justifyContent="space-between">
              <OutlinedInput
                name="search"
                onChange={handleSearch}
                placeholder="Search Asset"
                endAdornment={
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: color.primary[600] }} />
                  </InputAdornment>
                }
                inputProps={{
                  style: { color: color.grey[700] }
                }}
              />
            </Box>
          )}

        {assets.length === 0 &&
          !isLoading &&
          (isEmpty(search)
            ? (
              <Box>
                <Typography
                  color={color.grey[900]}
                  fontWeight={400}
                  fontSize={18}
                >
                  Upload your first asset
                </Typography>

                <Typography
                  color={color.grey[700]}
                  fontWeight={400}
                  fontSize={16}
                >
                  You haven&apos;t upload any asset.
                </Typography>
                <Typography
                  color={color.grey[700]}
                  fontWeight={400}
                  fontSize={16}
                >
                  If this is an error please
                  <Link
                    ml={0.5}
                    href={`mailto:${ADMIN_CONTACT_EMAIL}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Typography
                      component="span"
                      sx={{ textDecoration: 'underline' }}
                      color={color.primary[600]}
                      fontWeight={400}
                      fontSize={16}
                    >
                      Contact Us.
                    </Typography>
                  </Link>
                </Typography>
              </Box>
            )
            : (
              <Box mt={3}>
                <Typography
                  color={color.grey[900]}
                  fontWeight={500}
                  fontSize={18}
                >
                  No results found.
                </Typography>
              </Box>
            ))}

        {!isEmpty(selectedAssets) && (
          <Box display="flex" gap={2} pt={1}>
            <Typography color={color.grey[700]} fontWeight={400} fontSize={16}>
              {`${selectedAssets.length} item(s) selected`}
            </Typography>

            <MButton
              component="label"
              type="button"
              size="medium"
              rounded="true"
              sx={{ padding: 0 }}
              texttransform="none"
              onClick={() => {
                setSelectedAssets(allAssets)
              }}
            >
              <Typography fontSize={16} color={color.grey[900]}>
                Select All
              </Typography>
            </MButton>

            <MButton
              component="label"
              type="button"
              size="medium"
              rounded="true"
              sx={{ padding: 0 }}
              texttransform="none"
              onClick={async () => {
                await handleMultipleDelete(true)
              }}
            >
              <Typography fontSize={16} color={color.error[500]}>
                Delete selected
              </Typography>
            </MButton>

            <MButton
              component="label"
              type="button"
              size="medium"
              rounded="true"
              sx={{ padding: 0 }}
              texttransform="none"
              onClick={() => {
                setSelectedAssets([])
              }}
            >
              <Typography fontSize={16} color={color.grey[900]}>
                Clear Selection
              </Typography>
            </MButton>
          </Box>
        )}

        {isLoading
          ? (
            <ProgressLoaders />
          )
          : (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box py={3}>
                <Grid container spacing={3}>
                  {assets.map((item, index) => (
                    <Grid key={index} item xs={12} sm={6} md={4}>
                      <AssetListCard
                        id={index}
                        filename={item.key}
                        url={item.signedUrl}
                        updatedAt={item.lastModifiedDate}
                        selectedAssets={selectedAssets}
                        setSelectedAssets={setSelectedAssets}
                        setIsLoading={setIsLoading}
                        handleAssets={handleAssets}
                        setDialogData={setDialogData}
                      />
                    </Grid>
                  ))}

                  {(assets.length < 9 || assets.length === 0) && (
                    <Grid item xs={12} sm={6} md={4}>
                      <UploadAssetCard
                        setIsLoading={setIsLoading}
                        handleAssets={handleAssets}
                        setDialogData={setDialogData}
                        setEditCarouselData={setEditCarouselData}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              {allAssets.length > 9 && (
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={handlePagination}
                />
              )}
            </Box>
          )}
      </Box>

      <AlertDialog
        open={dialogData.isOpen}
        type={dialogData.type}
        title={dialogData.title}
        message={dialogData.message}
        handleClick={() => {
          setDialogData({ ...dialogData, isOpen: false })
          void handleAssets()
        }}
      >
        <MButton
          variant="contained"
          type="button"
          rounded="true"
          texttransform="none"
          onClick={() => {
            setDialogData({ ...dialogData, isOpen: false })
            void handleAssets()
          }}
        >
          Go Back to Assets
        </MButton>
      </AlertDialog>

      <AlertDialog
        open={confirmDialog.isOpen}
        type={confirmDialog.type}
        title={confirmDialog.title}
        message={confirmDialog.message}
        handleClick={() => {
          setConfirmDialog({ ...confirmDialog, isOpen: false })
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          gap={1}
        >
          <MButton
            variant="contained"
            type="button"
            rounded="true"
            texttransform="none"
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, isOpen: false })
              void handleMultipleDelete(false)
            }}
          >
            {'Delete Asset(s)'}
          </MButton>
          <MButton
            variant="outlined"
            type="button"
            rounded="true"
            texttransform="none"
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, isOpen: false })
            }}
          >
            Go Back to Assets List
          </MButton>
        </Box>
      </AlertDialog>

      {editCarouselData.isOpen && (
        <EditAssetCarousel
          isOpen={editCarouselData.isOpen}
          onClose={handleCloseCarousel}
          failedFiles={editCarouselData.failedFiles}
          fileData={editCarouselData.fileData}
        />
      )}
    </Box>
  )
}
